/**
 * @fileoverview Present recorded application activity in a bounded workspace with shared filters, inline totals, and switchable event, feature, and people tables. The backend owns event capture, filtering, and bounded pagination; this workspace owns draft/applied filters and inspection. Audit evidence covers instrumented behavior rather than all activity. `AiSpendDashboard.js` separately owns provider billing and AI cost attribution.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import api from '../../api'
import { AppButton } from '../shared'
import { AdminEmptyState, AdminNote } from './AdminChrome'
import './UsageAuditDashboard.css'


const PAGE_SIZE = 50



function dateInputValue(date) {
    return date.toISOString().slice(0, 10)
}



function initialFilters() {
    const end = new Date()
    const start = new Date(end)
    start.setDate(start.getDate() - 30)
    return {
        start: dateInputValue(start),
        end: dateInputValue(end),
        feature: '',
        action: '',
        actor: '',
        outcome: '',
        q: '',
    }
}



function formatNumber(value) {
    return Number(value || 0).toLocaleString()
}



function formatDuration(value) {
    const milliseconds = Number(value || 0)
    if (milliseconds < 1000) return `${formatNumber(milliseconds)} ms`
    return `${(milliseconds / 1000).toFixed(milliseconds < 10000 ? 1 : 0)} s`
}



function formatTimestamp(value) {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(date)
}



function labelFromKey(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
        .replace(/\bId\b/g, 'ID')
}



function actorLabel(actor = {}) {
    return actor.name || actor.email || (actor.user_id ? `User ${actor.user_id}` : 'System')
}



function choiceSummary(event) {
    const entries = Object.entries(event.input_choices || {})
    if (!entries.length) return '—'
    return entries.slice(0, 3).map(([key, value]) => `${labelFromKey(key)}: ${String(value)}`).join(' · ')
}



function identifierSummary(event) {
    const entries = Object.entries(event.entity_ids || {})
    if (!entries.length) return '—'
    return entries.map(([key, value]) => `${labelFromKey(key)} ${String(value)}`).join(' · ')
}



function UsageAuditDashboard({ embedded = false }) {
    const [draftFilters, setDraftFilters] = useState(initialFilters)
    const [filters, setFilters] = useState(initialFilters)
    const [page, setPage] = useState(1)
    const [dashboard, setDashboard] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [view, setView] = useState('events')
    const [expandedEvent, setExpandedEvent] = useState(null)
    const resultsRef = useRef(null)

    const loadDashboard = useCallback(

    async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get('/admin/audit-dashboard', {
                params: {
                    ...filters,
                    page,
                    page_size: PAGE_SIZE,
                },
            })
            setDashboard(response.data)
        } catch (requestError) {
            console.error('Failed to load usage audit dashboard:', requestError)
            setError(requestError.response?.data?.error || 'Usage audit dashboard failed to load.')
        } finally {
            setLoading(false)
        }
    }, [filters, page])

    useEffect(() => {
        loadDashboard()
    }, [loadDashboard])

    const summary = dashboard?.summary || {}
    const pagination = dashboard?.pagination || { page: 1, page_size: PAGE_SIZE, total: 0 }
    const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.page_size || PAGE_SIZE)))
    const actorOptions = useMemo(() => {
        const seen = new Set()
        return (dashboard?.people || []).filter(

        (person) => {
            const value = person.user_id || person.email || String(person.employee_id || '')
            if (!value || seen.has(value)) return false
            seen.add(value)
            return true
        }).map((person) => ({
            value: person.user_id || person.email || String(person.employee_id),
            label: person.name || person.email || `User ${person.user_id}`,
        }))
    }, [dashboard])



    function updateDraft(key, value) {
        setDraftFilters((current) => ({ ...current, [key]: value }))
    }



    function applyFilters(event) {
        event.preventDefault()
        setPage(1)
        setFilters({ ...draftFilters })
    }



    function clearFilters() {
        const next = initialFilters()
        setDraftFilters(next)
        setFilters(next)
        setPage(1)
    }

    useEffect(() => {
        if (resultsRef.current) resultsRef.current.scrollTop = 0
        setExpandedEvent(null)
    }, [filters, page, view])

    const views = [
        ['events', 'Event history', 'pi pi-list'],
        ['features', 'Feature & action', 'pi pi-chart-bar'],
        ['people', 'People', 'pi pi-users'],
    ]

    return (
        <div className={`usage-audit-workspace${embedded ? ' is-embedded' : ''}`}>
            <header className="usage-audit-header">
                <h2>Activity audit</h2>
                <dl className="usage-audit-metrics" aria-label="Range summary">
                    <div><dt>Events</dt><dd>{formatNumber(summary.events)}</dd></div>
                    <div><dt>Active people</dt><dd>{formatNumber(summary.active_people)}</dd></div>
                    <div className={summary.failures ? 'usage-audit-metric-failures' : ''} title={`${formatNumber(summary.failures)} failed events`}>
                        <dt>Failure rate</dt><dd>{Number(summary.failure_rate || 0).toFixed(1)}%</dd>
                    </div>
                    <div><dt>Avg. time</dt><dd>{formatDuration(summary.average_duration_ms)}</dd></div>
                </dl>
                <AppButton variant="secondary" size="small" icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
                    aria-label="Refresh activity" tooltip="Refresh activity" disabled={loading} onClick={loadDashboard} />
            </header>
            {error ? <AdminNote tone="warning" title="Dashboard unavailable">{error}</AdminNote> : null}

            <form aria-label="Filter activity" className="usage-audit-filters" onSubmit={applyFilters}>
                <label>
                    <span>From</span>
                    <input type="date" value={draftFilters.start} onChange={(event) => updateDraft('start', event.target.value)} />
                </label>
                <label>
                    <span>To</span>
                    <input type="date" value={draftFilters.end} onChange={(event) => updateDraft('end', event.target.value)} />
                </label>
                <label>
                    <span>Feature</span>
                    <select value={draftFilters.feature} onChange={(event) => updateDraft('feature', event.target.value)}>
                        <option value="">All features</option>
                        {(dashboard?.options?.features || []).map((feature) => <option key={feature} value={feature}>{feature}</option>)}
                    </select>
                </label>
                <label>
                    <span>Action</span>
                    <select value={draftFilters.action} onChange={(event) => updateDraft('action', event.target.value)}>
                        <option value="">All actions</option>
                        {(dashboard?.options?.actions || []).map((action) => <option key={action} value={action}>{labelFromKey(action)}</option>)}
                    </select>
                </label>
                <label>
                    <span>Person</span>
                    <select value={draftFilters.actor} onChange={(event) => updateDraft('actor', event.target.value)}>
                        <option value="">All people</option>
                        {actorOptions.map((person) => <option key={person.value} value={person.value}>{person.label}</option>)}
                    </select>
                </label>
                <label>
                    <span>Outcome</span>
                    <select value={draftFilters.outcome} onChange={(event) => updateDraft('outcome', event.target.value)}>
                        <option value="">All outcomes</option>
                        <option value="success">Success</option>
                        <option value="failure">Failure</option>
                    </select>
                </label>
                <label className="usage-audit-search">
                    <span>Search</span>
                    <input
                        type="search"
                        value={draftFilters.q}
                        placeholder="Person, route, matter, lead, document…"
                        onChange={(event) => updateDraft('q', event.target.value)}
                    />
                </label>
                <div className="usage-audit-filter-actions">
                    <AppButton type="submit" size="small" icon="pi pi-filter" label="Apply filters" />
                    <AppButton type="button" size="small" variant="secondary" label="Clear" onClick={clearFilters} />
                </div>
            </form>

            <div className="usage-audit-viewbar">
                <nav className="usage-audit-views" aria-label="Audit views">
                    {views.map(([key, label, icon]) => (
                        <button key={key} type="button" aria-pressed={view === key} onClick={() => setView(key)}>
                            <i className={icon} aria-hidden="true" />{label}
                        </button>
                    ))}
                </nav>
                {view === 'events' ? (
                    <div className="usage-audit-pagination">
                        <span className="usage-audit-page-label">{formatNumber(pagination.total)} events · Page {pagination.page || 1} of {formatNumber(totalPages)}</span>
                        <AppButton variant="secondary" size="small" icon="pi pi-chevron-left" aria-label="Previous page" tooltip="Previous page" disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))} />
                        <AppButton variant="secondary" size="small" icon="pi pi-chevron-right" aria-label="Next page" tooltip="Next page" disabled={page >= totalPages || loading} onClick={() => setPage((value) => value + 1)} />
                    </div>
                ) : <span className="usage-audit-page-label">{formatNumber((dashboard?.[view] || []).length)} {view === 'features' ? 'actions' : 'people'}</span>}
            </div>
            <section ref={resultsRef} className="usage-audit-results" aria-label={views.find(([key]) => key === view)[1]} aria-busy={loading}>
                {view === 'features' ? (
                    <div className="usage-audit-table-wrap">
                        <table className="usage-audit-table" aria-label="Feature activity">
                            <thead>
                                <tr>
                                    <th>Feature</th>
                                    <th>Action</th>
                                    <th>Events</th>
                                    <th>Failure</th>
                                    <th>Avg. time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(dashboard?.features || []).map((row) => (
                                    <tr key={`${row.feature}:${row.action}`}>
                                        <td><span className={`usage-audit-feature usage-audit-feature-${row.feature.toLowerCase()}`}>{row.feature}</span></td>
                                        <td className="usage-audit-truncate" title={labelFromKey(row.action)}>{labelFromKey(row.action)}</td>
                                        <td>{formatNumber(row.events)}</td>
                                        <td className={row.failures ? 'usage-audit-danger' : ''}>{Number(row.failure_rate || 0).toFixed(1)}%</td>
                                        <td>{formatDuration(row.average_duration_ms)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!loading && !(dashboard?.features || []).length ? (
                            <div className="usage-audit-empty-wrap"><AdminEmptyState title="No feature activity" description="No events match this range and filter set." /></div>
                            ) : null}
                        </div>
                ) : null}
                {view === 'people' ? (
                    <div className="usage-audit-table-wrap">
                        <table className="usage-audit-table" aria-label="People activity">
                            <thead>
                                <tr>
                                    <th>Person</th>
                                    <th>Events</th>
                                    <th>Failures</th>
                                    <th>Last seen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(dashboard?.people || []).map((person) => (
                                    <tr key={`${person.user_id || person.email || 'system'}:${person.employee_id || ''}`}>
                                        <td>
                                            <div className="usage-audit-person">
                                                <span className="usage-audit-avatar" aria-hidden="true">{(person.name || person.email || 'S').charAt(0).toUpperCase()}</span>
                                                <span>
                                                    <strong title={person.email || undefined}>{person.name || person.email || 'System'}</strong>
                                                </span>
                                            </div>
                                        </td>
                                        <td>{formatNumber(person.events)}</td>
                                        <td className={person.failures ? 'usage-audit-danger' : ''}>{formatNumber(person.failures)}</td>
                                        <td>{formatTimestamp(person.last_seen_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!loading && !(dashboard?.people || []).length ? (
                            <div className="usage-audit-empty-wrap"><AdminEmptyState title="No employee activity" description="No identified people match these filters." /></div>
                            ) : null}
                        </div>
                ) : null}
                {view === 'events' ? (
                    <div className="usage-audit-table-wrap">
                        <table className="usage-audit-table usage-audit-history" aria-label="Event history">
                            <colgroup>
                                {[12, 14, 24, 14, 15, 10, 7, 4].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Person</th>
                                    <th>Feature / action</th>
                                    <th>Related record</th>
                                    <th>Choices</th>
                                    <th>Outcome</th>
                                    <th>Time</th>
                                    <th aria-label="Details"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {(dashboard?.events || []).map((event) => (
                                    <React.Fragment key={event.id}>
                                        <tr className={expandedEvent === event.id ? 'is-expanded' : undefined}>
                                            <td className="usage-audit-nowrap">{formatTimestamp(event.occurred_at)}</td>
                                            <td className="usage-audit-truncate" title={actorLabel(event.actor)}>{actorLabel(event.actor)}</td>
                                            <td title={`${event.feature} · ${labelFromKey(event.action)}`}>
                                                <div className="usage-audit-action"><span className={`usage-audit-feature usage-audit-feature-${event.feature.toLowerCase()}`}>{event.feature}</span><span>{labelFromKey(event.action)}</span></div>
                                            </td>
                                            <td className="usage-audit-truncate" title={identifierSummary(event)}>{identifierSummary(event)}</td>
                                            <td className="usage-audit-truncate" title={choiceSummary(event)}>{choiceSummary(event)}</td>
                                            <td>
                                                <span className={`usage-audit-outcome usage-audit-outcome-${event.outcome}`}>
                                                    <i className={`pi ${event.outcome === 'success' ? 'pi-check-circle' : 'pi-exclamation-circle'}`} aria-hidden="true" />
                                                    {labelFromKey(event.outcome)}
                                                </span>
                                            </td>
                                            <td>{formatDuration(event.duration_ms)}</td>
                                            <td>
                                                <button className="usage-audit-details-toggle" type="button" aria-label={`Details for event ${event.id}`}
                                                    aria-expanded={expandedEvent === event.id} aria-controls={`audit-event-${event.id}`}
                                                    onClick={() => setExpandedEvent((current) => current === event.id ? null : event.id)}>
                                                    <i className={`pi ${expandedEvent === event.id ? 'pi-chevron-up' : 'pi-ellipsis-h'}`} aria-hidden="true" />
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedEvent === event.id ? (
                                            <tr className="usage-audit-detail-row" id={`audit-event-${event.id}`}>
                                                <td colSpan={8}>
                                                    <dl>
                                                        <dt>Person</dt><dd>{actorLabel(event.actor)}{event.actor?.email ? ` · ${event.actor.email}` : ''}</dd>
                                                        <dt>Action</dt><dd>{event.feature} · {labelFromKey(event.action)}</dd>
                                                        <dt>Records</dt><dd>{identifierSummary(event)}</dd>
                                                        <dt>Choices</dt><dd>{Object.entries(event.input_choices || {}).map(([key, value]) => `${labelFromKey(key)}: ${String(value)}`).join(' · ') || '—'}</dd>
                                                        <dt>Route</dt><dd><code>{event.http_method} {event.route}</code></dd>
                                                        <dt>Correlation</dt><dd><code>{event.correlation_id}</code></dd>
                                                        {event.handoff_id ? <><dt>Handoff</dt><dd><code>{event.handoff_id}</code></dd></> : null}
                                                        {event.error ? <><dt>Error</dt><dd>{event.error.code}: {event.error.message}</dd></> : null}
                                                    </dl>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                        {!loading && !(dashboard?.events || []).length ? (
                            <div className="usage-audit-empty-wrap"><AdminEmptyState title="No audit events" description="Try a broader date range or clear one of the filters." /></div>
                        ) : null}
                    </div>
                ) : null}
            </section>
        </div>
    )
}

export default UsageAuditDashboard
